import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Building2, Users, FileText, ClipboardList, Plus, Power, PowerOff, BarChart3, LogOut, Shield, Trash2, AlertTriangle, Eye, EyeOff, Pencil, KeyRound, Copy } from 'lucide-react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api/superadmin' });

const SuperAdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem('sa_token'));
  const [user, setUser] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // agency object or null
  const [deleteText, setDeleteText] = useState('');
  const [editAgency, setEditAgency] = useState(null); // agency object being edited
  const [editForm, setEditForm] = useState({ name: '', max_users: 10, contact_email: '', contact_phone: '' });
  const [resetAgency, setResetAgency] = useState(null); // agency whose admin password is being reset
  const [resetAdmins, setResetAdmins] = useState([]); // list of admins for that agency
  const [resetTargetUserId, setResetTargetUserId] = useState('');
  const [resetCustomPassword, setResetCustomPassword] = useState('');
  const [resetResult, setResetResult] = useState(null); // {username, email, new_password}
  const [form, setForm] = useState({ name: '', admin_email: '', admin_password: '', admin_name: '', plan: 'basic', max_users: 10, contact_email: '', contact_phone: '' });

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Force dark theme for Super Admin panel regardless of global theme
  useEffect(() => {
    const prevTheme = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'dark');
    return () => {
      if (prevTheme) document.documentElement.setAttribute('data-theme', prevTheme);
      else document.documentElement.removeAttribute('data-theme');
    };
  }, []);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Validate token
      api.get('/stats').then(r => {
        setStats(r.data);
        setUser(JSON.parse(localStorage.getItem('sa_user') || '{}'));
      }).catch(() => { localStorage.removeItem('sa_token'); localStorage.removeItem('sa_user'); setToken(null); });
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [a, s] = await Promise.all([api.get('/agencies'), api.get('/stats')]);
      setAgencies(a.data);
      setStats(s.data);
    } catch { toast.error('Error cargando datos'); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await api.post('/login', { email: loginEmail, password: loginPassword });
      localStorage.setItem('sa_token', res.data.token);
      localStorage.setItem('sa_user', JSON.stringify(res.data.user));
      api.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      setToken(res.data.token);
      setUser(res.data.user);
      toast.success('Bienvenido, Super Admin');
    } catch { toast.error('Credenciales incorrectas'); }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('sa_token');
    localStorage.removeItem('sa_user');
    setToken(null);
    setUser(null);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/agencies', form);
      toast.success('Agencia creada exitosamente');
      setCreateOpen(false);
      setForm({ name: '', admin_email: '', admin_password: '', admin_name: '', plan: 'basic', max_users: 10, contact_email: '', contact_phone: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear agencia'); }
  };

  const toggleAgency = async (agencyId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await api.patch(`/agencies/${agencyId}`, { status: newStatus });
      toast.success(`Agencia ${newStatus === 'active' ? 'activada' : 'desactivada'}`);
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleDeleteAgency = async () => {
    if (!deleteConfirm) return;
    if (deleteText !== deleteConfirm.name) {
      toast.error('El nombre no coincide. Escribe el nombre exacto de la agencia.');
      return;
    }
    try {
      await api.delete(`/agencies/${deleteConfirm.agency_id}`);
      toast.success(`Agencia "${deleteConfirm.name}" eliminada permanentemente`);
      setDeleteConfirm(null);
      setDeleteText('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar agencia');
    }
  };

  const openEdit = (a) => {
    setEditAgency(a);
    setEditForm({
      name: a.name || '',
      max_users: a.max_users || 10,
      contact_email: a.contact_email || '',
      contact_phone: a.contact_phone || ''
    });
  };

  const handleEditSave = async () => {
    if (!editAgency) return;
    if (!editForm.name.trim()) { toast.error('El nombre es requerido'); return; }
    const max = parseInt(editForm.max_users);
    if (isNaN(max) || max < 1) { toast.error('Max usuarios debe ser un numero mayor a 0'); return; }
    try {
      await api.patch(`/agencies/${editAgency.agency_id}`, {
        name: editForm.name.trim(),
        max_users: max,
        contact_email: editForm.contact_email.trim(),
        contact_phone: editForm.contact_phone.trim()
      });
      toast.success('Agencia actualizada');
      setEditAgency(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar agencia');
    }
  };

  const openResetPassword = async (a) => {
    setResetAgency(a);
    setResetTargetUserId('');
    setResetCustomPassword('');
    setResetResult(null);
    try {
      const r = await api.get(`/agencies/${a.agency_id}/admins`);
      setResetAdmins(r.data || []);
      if (r.data && r.data.length > 0) setResetTargetUserId(r.data[0].user_id);
    } catch {
      toast.error('Error al cargar admins de la agencia');
      setResetAdmins([]);
    }
  };

  const handleResetPasswordSubmit = async () => {
    if (!resetAgency || !resetTargetUserId) {
      toast.error('Selecciona un usuario admin');
      return;
    }
    if (resetCustomPassword && resetCustomPassword.length < 6) {
      toast.error('La contrasena debe tener al menos 6 caracteres (o dejala vacia para auto-generar)');
      return;
    }
    try {
      const r = await api.post(
        `/agencies/${resetAgency.agency_id}/users/${resetTargetUserId}/reset-password`,
        { password: resetCustomPassword || '' }
      );
      setResetResult(r.data);
      toast.success('Contrasena restablecida');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al restablecer contrasena');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copiado al portapapeles'));
  };

  const [showPassword, setShowPassword] = useState(false);

  // --- LOGIN SCREEN ---
  if (!token) {
    return (
      <div className="sa-panel min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-orange-500/10 mb-4">
              <Shield size={32} className="text-orange-500" />
            </div>
            <h1 className="text-2xl font-bold sa-heading">Super Admin</h1>
            <p className="text-zinc-500 text-sm mt-1">Panel de administracion de agencias</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="text-zinc-400">Email</Label>
              <Input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="tu@email.com" className="bg-zinc-900 border-zinc-800" required data-testid="sa-login-email" />
            </div>
            <div>
              <Label className="text-zinc-400">Contrasena</Label>
              <div className="relative">
                <Input type={showPassword ? "text" : "password"} value={loginPassword} onChange={e => setLoginPassword(e.target.value)} placeholder="********" className="bg-zinc-900 border-zinc-800 pr-10" required data-testid="sa-login-password" />
                <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200" data-testid="sa-login-toggle-password">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" disabled={loginLoading} data-testid="sa-login-btn">
              {loginLoading ? 'Accediendo...' : 'Acceder'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // --- DASHBOARD ---
  return (
    <div className="sa-panel min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="border-b border-zinc-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
              <Shield size={20} className="text-orange-500" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Super Admin</h1>
              <p className="text-xs text-zinc-500">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout} className="border-zinc-700 text-zinc-400 hover:text-white" data-testid="sa-logout">
            <LogOut size={14} className="mr-1" /> Cerrar sesion
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="sa-stats">
            <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-4">
              <div className="flex items-center gap-3"><Building2 size={20} className="text-orange-400" /><div><p className="text-2xl font-bold text-white">{stats.agencies?.total || 0}</p><p className="text-xs text-zinc-500">Agencias ({stats.agencies?.active || 0} activas)</p></div></div>
            </CardContent></Card>
            <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-4">
              <div className="flex items-center gap-3"><Users size={20} className="text-blue-400" /><div><p className="text-2xl font-bold text-white">{stats.users || 0}</p><p className="text-xs text-zinc-500">Usuarios totales</p></div></div>
            </CardContent></Card>
            <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-4">
              <div className="flex items-center gap-3"><FileText size={20} className="text-green-400" /><div><p className="text-2xl font-bold text-white">{stats.content || 0}</p><p className="text-xs text-zinc-500">Contenido total</p></div></div>
            </CardContent></Card>
            <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-4">
              <div className="flex items-center gap-3"><ClipboardList size={20} className="text-purple-400" /><div><p className="text-2xl font-bold text-white">{stats.work_orders || 0}</p><p className="text-xs text-zinc-500">Ordenes de trabajo</p></div></div>
            </CardContent></Card>
          </div>
        )}

        {/* Agencies List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Agencias</h2>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-orange-500 hover:bg-orange-600" data-testid="sa-create-agency-btn"><Plus size={16} className="mr-1" /> Nueva Agencia</Button>
              </DialogTrigger>
              <DialogContent className="sa-dialog bg-zinc-900 border-zinc-700 max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="text-white">Crear Nueva Agencia</DialogTitle></DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div>
                    <Label className="text-zinc-400">Nombre de la agencia</Label>
                    <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="bg-zinc-800 border-zinc-700" required data-testid="sa-agency-name" />
                  </div>
                  <div className="border-t border-zinc-700 pt-4">
                    <p className="text-sm font-medium text-zinc-300 mb-3">Administrador de la agencia</p>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-zinc-400">Nombre del admin</Label>
                        <Input value={form.admin_name} onChange={e => setForm(p => ({...p, admin_name: e.target.value}))} className="bg-zinc-800 border-zinc-700" data-testid="sa-admin-name" />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Email del admin</Label>
                        <Input type="email" value={form.admin_email} onChange={e => setForm(p => ({...p, admin_email: e.target.value}))} className="bg-zinc-800 border-zinc-700" required data-testid="sa-admin-email" />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Contrasena del admin</Label>
                        <Input type="password" value={form.admin_password} onChange={e => setForm(p => ({...p, admin_password: e.target.value}))} className="bg-zinc-800 border-zinc-700" required data-testid="sa-admin-password" />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400">Plan</Label>
                      <Select value={form.plan} onValueChange={v => setForm(p => ({...p, plan: v}))}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          <SelectItem value="basic">Basico</SelectItem>
                          <SelectItem value="pro">Pro</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-zinc-400">Max usuarios</Label>
                      <Input type="number" value={form.max_users} onChange={e => setForm(p => ({...p, max_users: parseInt(e.target.value) || 10}))} className="bg-zinc-800 border-zinc-700" data-testid="sa-max-users" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-zinc-400">Telefono de contacto (opcional)</Label>
                    <Input value={form.contact_phone} onChange={e => setForm(p => ({...p, contact_phone: e.target.value}))} className="bg-zinc-800 border-zinc-700" />
                  </div>
                  <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="sa-create-submit">Crear Agencia</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500" /></div>
          ) : (
            <div className="grid gap-4">
              {agencies.map(a => (
                <Card key={a.agency_id} className="bg-zinc-900 border-zinc-800" data-testid={`agency-card-${a.agency_id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0">
                          <Building2 size={22} className="text-orange-400" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-white">{a.name}</h3>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${a.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{a.status === 'active' ? 'Activa' : 'Inactiva'}</span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-300 font-medium">{a.plan}</span>
                            {a.is_default && <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 font-medium">Principal</span>}
                          </div>
                          <p className="text-xs text-zinc-500 mt-0.5">{a.contact_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="flex gap-5 text-center">
                          <div><p className="text-lg font-bold sa-white">{a.user_count || 0}<span className="text-zinc-500 text-sm font-normal"> / {a.max_users || 10}</span></p><p className="text-[10px] text-zinc-500">Usuarios</p></div>
                          <div><p className="text-lg font-bold sa-white">{a.content_count || 0}</p><p className="text-[10px] text-zinc-500">Contenido</p></div>
                          <div><p className="text-lg font-bold sa-white">{a.task_count || 0}</p><p className="text-[10px] text-zinc-500">Tareas</p></div>
                          <div><p className="text-lg font-bold sa-white">{a.wo_count || 0}</p><p className="text-[10px] text-zinc-500">Ordenes</p></div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => openEdit(a)}
                          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                          data-testid={`edit-agency-${a.agency_id}`}>
                          <Pencil size={14} className="mr-1" /> Editar
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openResetPassword(a)}
                          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                          data-testid={`reset-pw-agency-${a.agency_id}`}>
                          <KeyRound size={14} className="mr-1" /> Contrasena
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => toggleAgency(a.agency_id, a.status)}
                          className={a.status === 'active' ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-green-500/30 text-green-400 hover:bg-green-500/10'}
                          data-testid={`toggle-agency-${a.agency_id}`}>
                          {a.status === 'active' ? <><PowerOff size={14} className="mr-1" /> Desactivar</> : <><Power size={14} className="mr-1" /> Activar</>}
                        </Button>
                        {!a.is_default && (
                          <Button variant="outline" size="sm" onClick={() => { setDeleteConfirm(a); setDeleteText(''); }}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            data-testid={`delete-agency-${a.agency_id}`}>
                            <Trash2 size={14} className="mr-1" /> Eliminar
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {agencies.length === 0 && <p className="text-zinc-500 text-center py-8">No hay agencias registradas</p>}
            </div>
          )}
        </div>
      </main>

      {/* Reset Password Dialog (Super Admin) */}
      <Dialog open={!!resetAgency} onOpenChange={(open) => { if (!open) { setResetAgency(null); setResetResult(null); } }}>
        <DialogContent className="sa-dialog bg-zinc-900 border-blue-500/40 max-w-md">
          <DialogHeader>
            <DialogTitle className="sa-white flex items-center gap-2">
              <KeyRound size={18} /> Restablecer contrasena
            </DialogTitle>
          </DialogHeader>
          {!resetResult ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3">
                <p className="text-xs text-blue-200">
                  Agencia: <strong>{resetAgency?.name}</strong>. Al restablecer, todas las sesiones activas de ese usuario se cerraran.
                </p>
              </div>
              <div>
                <Label className="text-zinc-400">Usuario admin a restablecer</Label>
                {resetAdmins.length === 0 ? (
                  <p className="text-sm text-zinc-500 mt-1">Esta agencia no tiene admins registrados.</p>
                ) : (
                  <select
                    value={resetTargetUserId}
                    onChange={(e) => setResetTargetUserId(e.target.value)}
                    className="w-full mt-1 rounded-md bg-zinc-800 border border-zinc-700 px-3 py-2"
                    data-testid="sa-reset-user-select"
                  >
                    {resetAdmins.map(adm => (
                      <option key={adm.user_id} value={adm.user_id}>
                        {adm.name ? `${adm.name} - ${adm.email}` : adm.email}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <Label className="text-zinc-400">Nueva contrasena (opcional - se genera automatica si la dejas vacia)</Label>
                <Input
                  type="text"
                  value={resetCustomPassword}
                  onChange={(e) => setResetCustomPassword(e.target.value)}
                  placeholder="Minimo 6 caracteres"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="sa-reset-custom-password"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setResetAgency(null)} className="border-zinc-700 text-zinc-300" data-testid="sa-reset-cancel">
                  Cancelar
                </Button>
                <Button
                  onClick={handleResetPasswordSubmit}
                  disabled={resetAdmins.length === 0 || !resetTargetUserId}
                  className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40"
                  data-testid="sa-reset-submit"
                >
                  Restablecer
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-4">
                <p className="text-sm font-semibold text-green-300 mb-2">Contrasena restablecida con exito</p>
                <p className="text-xs text-green-200/80 mb-3">
                  Guarda o comparte estas credenciales con el administrador de la agencia.
                  Esta es la unica vez que veras la contrasena en texto claro.
                </p>
                <div className="space-y-2 bg-zinc-900/50 rounded p-3 font-mono text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400">Usuario:</span>
                    <div className="flex items-center gap-2">
                      <span className="sa-white">{resetResult.email || resetResult.username}</span>
                      <button onClick={() => copyToClipboard(resetResult.email || resetResult.username)} className="text-zinc-400 hover:text-white" data-testid="sa-reset-copy-user">
                        <Copy size={12} />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400">Nueva contrasena:</span>
                    <div className="flex items-center gap-2">
                      <span className="sa-white font-bold">{resetResult.new_password}</span>
                      <button onClick={() => copyToClipboard(resetResult.new_password)} className="text-zinc-400 hover:text-white" data-testid="sa-reset-copy-password">
                        <Copy size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => { setResetAgency(null); setResetResult(null); }} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="sa-reset-close">
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Agency Dialog */}
      <Dialog open={!!editAgency} onOpenChange={(open) => { if (!open) setEditAgency(null); }}>
        <DialogContent className="sa-dialog bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="sa-white flex items-center gap-2">
              <Pencil size={18} /> Editar agencia
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Nombre de la agencia</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm(p => ({...p, name: e.target.value}))} className="bg-zinc-800 border-zinc-700" data-testid="sa-edit-name" />
            </div>
            <div>
              <Label className="text-zinc-400">Max usuarios permitidos</Label>
              <Input type="number" min="1" value={editForm.max_users} onChange={(e) => setEditForm(p => ({...p, max_users: e.target.value}))} className="bg-zinc-800 border-zinc-700" data-testid="sa-edit-max-users" />
              {editAgency && (editAgency.user_count || 0) > 0 && (
                <p className="text-xs text-zinc-500 mt-1">Actualmente tiene {editAgency.user_count} usuario(s) registrado(s)</p>
              )}
            </div>
            <div>
              <Label className="text-zinc-400">Email de contacto</Label>
              <Input type="email" value={editForm.contact_email} onChange={(e) => setEditForm(p => ({...p, contact_email: e.target.value}))} className="bg-zinc-800 border-zinc-700" data-testid="sa-edit-contact-email" />
            </div>
            <div>
              <Label className="text-zinc-400">Telefono de contacto</Label>
              <Input value={editForm.contact_phone} onChange={(e) => setEditForm(p => ({...p, contact_phone: e.target.value}))} className="bg-zinc-800 border-zinc-700" data-testid="sa-edit-contact-phone" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditAgency(null)} className="border-zinc-700 text-zinc-300" data-testid="sa-edit-cancel">
                Cancelar
              </Button>
              <Button onClick={handleEditSave} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="sa-edit-save">
                Guardar cambios
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Agency Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) { setDeleteConfirm(null); setDeleteText(''); } }}>
        <DialogContent className="sa-dialog bg-zinc-900 border-red-500/40 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle size={20} /> Eliminar agencia permanentemente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
              <p className="text-sm text-red-300 font-semibold mb-2">Esta accion NO se puede deshacer.</p>
              <ul className="text-xs text-red-200/80 space-y-1 list-disc list-inside">
                <li>Se eliminara la agencia <strong>{deleteConfirm?.name}</strong></li>
                <li>Se borraran todos sus usuarios ({deleteConfirm?.user_count || 0})</li>
                <li>Se borrara todo el contenido, tareas, ordenes y datos asociados</li>
                <li>Los archivos subidos seran eliminados</li>
              </ul>
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Para confirmar, escribe el nombre exacto de la agencia:</Label>
              <p className="text-xs text-zinc-500 mb-2 mt-1">Nombre: <span className="font-mono text-orange-300">{deleteConfirm?.name}</span></p>
              <Input
                value={deleteText}
                onChange={(e) => setDeleteText(e.target.value)}
                placeholder={deleteConfirm?.name || ''}
                className="bg-zinc-800 border-zinc-700"
                data-testid="sa-delete-confirm-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setDeleteConfirm(null); setDeleteText(''); }} className="border-zinc-700 text-zinc-300" data-testid="sa-delete-cancel">
                Cancelar
              </Button>
              <Button
                onClick={handleDeleteAgency}
                disabled={deleteText !== deleteConfirm?.name}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="sa-delete-confirm-btn"
              >
                <Trash2 size={14} className="mr-1" /> Eliminar permanentemente
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminPage;
